import React, { useRef, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import {
  Box,
  Button,
  Divider,
  InputAdornment,
  Popover,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Calendar } from 'iconsax-react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import 'react-datepicker/dist/react-datepicker.css';
import './DateRangePicker.css';

export interface DateRangePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onChange: (start: Date | null, end: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
  minDate,
  maxDate,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  // Pending state — committed only when both dates are picked or Apply is clicked
  const [pendingRange, setPendingRange] = useState<[Date | null, Date | null]>([
    startDate,
    endDate,
  ]);

  const handleOpen = () => {
    if (disabled) return;
    setPendingRange([startDate, endDate]);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleCalendarChange = (range: [Date | null, Date | null]) => {
    setPendingRange(range);
    // Auto-close and commit once both dates are picked
    if (range[0] && range[1]) {
      onChange(range[0], range[1]);
      setOpen(false);
    }
  };

  const handleClear = () => {
    setPendingRange([null, null]);
    onChange(null, null);
    setOpen(false);
  };

  const formatDate = (d: Date | null) => (d ? format(d, 'dd/MM/yyyy') : '');

  const displayValue =
    startDate || endDate ? `${formatDate(startDate)}  →  ${formatDate(endDate)}` : '';

  return (
    <>
      <Box ref={anchorRef} onClick={handleOpen} sx={{ cursor: disabled ? 'default' : 'pointer' }}>
        <TextField
          size="small"
          fullWidth
          value={displayValue}
          placeholder={`${t('date_picker.start_date')} → ${t('date_picker.end_date')}`}
          disabled={disabled}
          InputProps={{
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                <Calendar size={18} color={theme.palette.text.secondary} />
              </InputAdornment>
            ),
            sx: { cursor: disabled ? 'default' : 'pointer', minWidth: 240 },
          }}
          sx={{ '& input': { cursor: disabled ? 'default' : 'pointer' } }}
        />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          elevation: 8,
          sx: { borderRadius: 2, overflow: 'visible', mt: 0.5 },
        }}
      >
        <Box sx={{ p: 1.5 }}>
          <ReactDatePicker
            selected={pendingRange[0]}
            startDate={pendingRange[0]}
            endDate={pendingRange[1]}
            onChange={handleCalendarChange as any}
            selectsRange
            inline
            monthsShown={isMobile ? 1 : 2}
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            minDate={minDate}
            maxDate={maxDate}
            calendarClassName="konekt-range-calendar"
          />
          <Divider sx={{ my: 1 }} />
          <Box display="flex" justifyContent="space-between" alignItems="center" px={0.5}>
            <Typography variant="caption" color="text.secondary">
              {pendingRange[0] && pendingRange[1]
                ? `${formatDate(pendingRange[0])} → ${formatDate(pendingRange[1])}`
                : pendingRange[0]
                ? `${formatDate(pendingRange[0])} → …`
                : t('date_picker.start_date')}
            </Typography>
            <Button size="small" color="inherit" onClick={handleClear}>
              {t('date_picker.clear')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default DateRangePicker;
